import { Layers, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  type OrgEventAutomationLogicBlock,
  type OrgEventAutomationLogicJoin,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  addAutomationCondition,
  getConditionById,
  removeAutomationCondition,
  updateAutomationCondition,
  updateGroupInternalJoin,
  updateLogicBlockJoin,
  type AutomationRuleLogicSlice,
} from './automationRuleLogic';
import { JOIN_OPTIONS, type SelectOption } from './ruleFormModel';
import { SimpleSelect } from './automationFormControls';
import { ConditionRow } from './ConditionRow';

/**
 * Filter-group builder for a rule's condition formula.
 *
 * The data model allows exactly two levels: top-level blocks joined pairwise by
 * `joinWithPrevious`, and groups that join their own conditions with a single
 * `internalJoin`. Groups cannot nest and blocks cannot be reordered, so this
 * builder deliberately offers neither. A group gets one join control on its
 * rail rather than one between every pair, since every pair shares the same
 * value.
 */
export function ConditionBuilder({
  slice,
  onChange,
  aorOptions,
}: {
  /** Already-normalized logic slice from form state. */
  slice: AutomationRuleLogicSlice;
  onChange: (next: AutomationRuleLogicSlice) => void;
  aorOptions: SelectOption[];
}) {
  const { conditions, logicBlocks } = slice;
  const canRemove = conditions.length > 1;

  const addCondition = (mode: 'compound' | 'independent') => {
    onChange(
      addAutomationCondition(slice, {
        mode,
        // A new group starts as AND; a new top-level block also joins with AND.
        join: 'and',
        preset: 'tropical',
      })
    );
  };

  const renderCondition = (conditionId: string) => {
    const condition = getConditionById(conditions, conditionId);
    if (!condition) return null;
    return (
      <ConditionRow
        condition={condition}
        canRemove={canRemove}
        aorOptions={aorOptions}
        onChange={(next) =>
          onChange(updateAutomationCondition(slice, conditionId, () => next))
        }
        onRemove={() => onChange(removeAutomationCondition(slice, conditionId))}
      />
    );
  };

  if (logicBlocks.length === 0) {
    return (
      <div className="tw:space-y-2">
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          No conditions yet. Add one to describe when this rule should fire.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="tw:gap-1"
          onClick={() => addCondition('independent')}
        >
          <Plus className="tw:h-3.5 tw:w-3.5" />
          Add condition
        </Button>
      </div>
    );
  }

  return (
    <div className="tw:space-y-2">
      {logicBlocks.map((block, blockIndex) => (
        <div
          key={block.type === 'group' ? block.id : block.conditionId}
          className="tw:space-y-2"
        >
          {blockIndex > 0 ? (
            <TopLevelJoin
              value={block.joinWithPrevious === 'or' ? 'or' : 'and'}
              onChange={(next) =>
                onChange(updateLogicBlockJoin(slice, blockIndex, next))
              }
            />
          ) : null}

          {block.type === 'condition' ? (
            renderCondition(block.conditionId)
          ) : (
            <GroupRail
              block={block}
              onJoinChange={(next) =>
                onChange(updateGroupInternalJoin(slice, block.id, next))
              }
            >
              {block.conditionIds.map((conditionId) => (
                <div key={conditionId}>{renderCondition(conditionId)}</div>
              ))}
            </GroupRail>
          )}
        </div>
      ))}

      <div className="tw:flex tw:flex-wrap tw:gap-2 tw:pt-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="tw:gap-1"
          onClick={() => addCondition('compound')}
        >
          <Plus className="tw:h-3.5 tw:w-3.5" />
          Add condition
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="tw:gap-1"
          onClick={() => addCondition('independent')}
        >
          <Layers className="tw:h-3.5 tw:w-3.5" />
          Add group
        </Button>
      </div>
      <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
        Add condition extends the last group, so it shares that group&rsquo;s
        AND/OR. Add group starts a separate one you join to the rest with its
        own AND/OR.
      </p>
    </div>
  );
}

/** The AND/OR between two top-level blocks. Independent per pair. */
function TopLevelJoin({
  value,
  onChange,
}: {
  value: OrgEventAutomationLogicJoin;
  onChange: (next: OrgEventAutomationLogicJoin) => void;
}) {
  return (
    <div className="tw:flex tw:items-center tw:gap-2">
      <div className="tw:h-px tw:w-4 tw:bg-border" />
      <SimpleSelect
        value={value}
        onChange={(next) => onChange(next === 'or' ? 'or' : 'and')}
        options={JOIN_OPTIONS}
        className="tw:w-20"
      />
      <div className="tw:h-px tw:flex-1 tw:bg-border" />
    </div>
  );
}

/**
 * Wraps a group's conditions with a left rail carrying the single join control
 * that applies to all of them.
 */
function GroupRail({
  block,
  onJoinChange,
  children,
}: {
  block: Extract<OrgEventAutomationLogicBlock, { type: 'group' }>;
  onJoinChange: (next: OrgEventAutomationLogicJoin) => void;
  children: React.ReactNode;
}) {
  const count = block.conditionIds.length;

  return (
    <div className="tw:flex tw:gap-2 tw:rounded-md tw:border tw:border-border tw:bg-muted/20 tw:p-2">
      <div className="tw:flex tw:w-20 tw:shrink-0 tw:flex-col tw:items-center tw:gap-1">
        <SimpleSelect
          value={block.internalJoin}
          onChange={(next) => onJoinChange(next === 'or' ? 'or' : 'and')}
          options={JOIN_OPTIONS}
          className="tw:w-20"
        />
        <div className="tw:w-px tw:flex-1 tw:bg-border" />
        <span className="tw:text-[10px] tw:text-muted-foreground">
          {count} in group
        </span>
      </div>
      <div className="tw:min-w-0 tw:flex-1 tw:space-y-2">{children}</div>
    </div>
  );
}
